import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Send,
  Sparkles,
  Lock,
  Loader2,
  Copy,
  Check,
  Trash2,
  Plus,
  ListChecks,
  MessageSquare,
  Library,
  BookOpen,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Calendar,
  ImagePlus,
  FileUp,
  Sunrise,
  Mail,
  Globe,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_ICON = {
  Instagram: Instagram,
  Facebook: Facebook,
  LinkedIn: Linkedin,
  YouTube: Youtube,
  TikTok: Sparkles,
  Pinterest: ImagePlus,
  X: Sparkles,
};

const QUICK_PROMPTS = [
  "What's my highest-priority task this week?",
  "Draft 5 Instagram posts inviting people to The Power Collective — schedule them across the next 14 days.",
  "Write a 3-day welcome email sequence for new E-Book buyers.",
  "Give me 10 hook ideas for a TikTok about the 4 Core Choices.",
  "Draft a LinkedIn post positioning me for high-ticket Recode Session clients.",
];

export default function AdminAssistantPage() {
  // Token gate
  const [token, setToken] = useState(() => localStorage.getItem('pb-admin-token') || '');
  const [tokenInput, setTokenInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authChecking, setAuthChecking] = useState(false);

  // Chat state
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Queue + knowledge + briefing
  const [queue, setQueue] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [knowledgeName, setKnowledgeName] = useState('');
  const [knowledgeContent, setKnowledgeContent] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingFocus, setBriefingFocus] = useState('');
  const [briefingEmail, setBriefingEmail] = useState('takingyourpowerback8@gmail.com');
  const [makeWebhook, setMakeWebhook] = useState('');
  const [makeWebhookInput, setMakeWebhookInput] = useState('');
  const [dispatchingId, setDispatchingId] = useState(null);

  // Content360 events (received via outgoing webhook from Content360)
  const [content360Events, setContent360Events] = useState([]);
  const [content360Loading, setContent360Loading] = useState(false);

  // Auth check on mount
  useEffect(() => {
    if (token) verifyToken(token);
  }, []); // eslint-disable-line

  const verifyToken = async (t) => {
    setAuthChecking(true);
    try {
      await axios.get(`${API}/admin/assistant/sessions?limit=1`, {
        headers: { 'X-Admin-Token': t },
      });
      setAuthed(true);
      localStorage.setItem('pb-admin-token', t);
      setToken(t);
      loadQueue(t);
      loadKnowledge(t);
      loadContent360Events(t);
    } catch (err) {
      setAuthed(false);
      localStorage.removeItem('pb-admin-token');
      if (err?.response?.status === 401) toast.error('Invalid admin token');
    } finally {
      setAuthChecking(false);
    }
  };

  const submitToken = (e) => {
    e.preventDefault();
    if (tokenInput.trim()) verifyToken(tokenInput.trim());
  };

  const logout = () => {
    localStorage.removeItem('pb-admin-token');
    setAuthed(false);
    setToken('');
    setTokenInput('');
    setMessages([]);
    setSessionId(null);
  };

  const loadQueue = async (t = token) => {
    try {
      const { data } = await axios.get(`${API}/admin/assistant/queue`, {
        headers: { 'X-Admin-Token': t },
      });
      setQueue(data.items || []);
    } catch {}
  };

  const loadKnowledge = async (t = token) => {
    try {
      const { data } = await axios.get(`${API}/admin/assistant/knowledge`, {
        headers: { 'X-Admin-Token': t },
      });
      setKnowledge(data.files || []);
    } catch {}
  };

  const loadMakeWebhook = async (t = token) => {
    try {
      const { data } = await axios.get(`${API}/admin/assistant/make-webhook`, {
        headers: { 'X-Admin-Token': t },
      });
      setMakeWebhook(data.webhook_url || '');
    } catch {}
  };

  const saveMakeWebhook = async (e) => {
    e.preventDefault();
    const trimmed = makeWebhookInput.trim();
    if (!trimmed.startsWith('https://')) {
      toast.error('Webhook URL must start with https://');
      return;
    }
    try {
      await axios.post(
        `${API}/admin/assistant/make-webhook`,
        { webhook_url: trimmed },
        { headers: { 'X-Admin-Token': token } },
      );
      setMakeWebhook(trimmed);
      setMakeWebhookInput('');
      toast.success('Webhook saved — Queue posts can now be dispatched');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const dispatchToMake = async (postId) => {
    setDispatchingId(postId);
    try {
      await axios.post(
        `${API}/admin/assistant/queue/${postId}/dispatch`,
        {},
        { headers: { 'X-Admin-Token': token } },
      );
      toast.success('Post sent to Make.com → Buffer → your socials 🚀');
      loadQueue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dispatch failed');
    } finally {
      setDispatchingId(null);
    }
  };

  const loadContent360Events = async (t = token) => {
    setContent360Loading(true);
    try {
      const { data } = await axios.get(`${API}/admin/assistant/content360-events?limit=100`, {
        headers: { 'X-Admin-Token': t },
      });
      setContent360Events(data.events || []);
    } catch {
      // silent — tab will just show empty state
    } finally {
      setContent360Loading(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/assistant/chat`,
        { session_id: sessionId, message: msg },
        { headers: { 'X-Admin-Token': token } },
      );
      setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.reply, queued: data.queued_posts || [] },
      ]);
      if (data.queued_posts?.length) {
        toast.success(`${data.queued_posts.length} post(s) added to your queue`);
        loadQueue();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'PB had trouble responding. Try again.');
      setMessages((prev) => prev.slice(0, -1));
      setInput(msg);
    } finally {
      setSending(false);
    }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
    setInput('');
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Caption copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const markPosted = async (postId) => {
    try {
      await axios.patch(
        `${API}/admin/assistant/queue/${postId}`,
        { status: 'posted' },
        { headers: { 'X-Admin-Token': token } },
      );
      loadQueue();
      toast.success('Marked as posted');
    } catch {
      toast.error('Failed to update');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post from the queue?')) return;
    try {
      await axios.delete(`${API}/admin/assistant/queue/${postId}`, {
        headers: { 'X-Admin-Token': token },
      });
      loadQueue();
      toast.success('Removed from queue');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const uploadKnowledge = async (e) => {
    e.preventDefault();
    if (!knowledgeName.trim() || !knowledgeContent.trim()) {
      toast.error('Name and content required');
      return;
    }
    try {
      await axios.post(
        `${API}/admin/assistant/knowledge`,
        { filename: knowledgeName.trim(), content: knowledgeContent },
        { headers: { 'X-Admin-Token': token } },
      );
      toast.success(`Added "${knowledgeName}" to PB's library`);
      setKnowledgeName('');
      setKnowledgeContent('');
      loadKnowledge();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
  };

  const deleteKnowledge = async (filename) => {
    if (!window.confirm(`Remove ${filename} from PB's library?`)) return;
    try {
      await axios.delete(`${API}/admin/assistant/knowledge/${filename}`, {
        headers: { 'X-Admin-Token': token },
      });
      loadKnowledge();
      toast.success('Removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files supported');
      return;
    }
    setUploadingPdf(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/assistant/knowledge/upload-pdf`, form, {
        headers: { 'X-Admin-Token': token, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Loaded "${data.saved}" — ${data.pages} pages, ${data.characters.toLocaleString()} chars`);
      loadKnowledge();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingPdf(false);
      e.target.value = '';
    }
  };

  const generateBriefing = async (sendEmail = false) => {
    setBriefingLoading(true);
    setBriefing(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/assistant/daily-briefing`,
        {
          focus_hint: briefingFocus || undefined,
          recipient_email: sendEmail ? briefingEmail : undefined,
        },
        { headers: { 'X-Admin-Token': token } },
      );
      setBriefing(data);
      if (data.emailed_to) toast.success(`Briefing emailed to ${data.emailed_to}`);
      else toast.success("Today's briefing ready");
      loadQueue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'PB had trouble generating the briefing');
    } finally {
      setBriefingLoading(false);
    }
  };

  // ---------- Token gate ----------
  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-dark via-primary to-maroon flex items-center justify-center p-4" data-testid="admin-gate">
        <Card className="max-w-md w-full p-8 bg-card border-secondary/30 shadow-2xl">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-maroon to-primary mx-auto mb-4 flex items-center justify-center shadow-lg">
              <Lock className="w-6 h-6 text-secondary" />
            </div>
            <h1 className="font-display text-2xl font-bold text-foreground">PB — Admin Console</h1>
            <p className="text-sm text-muted-foreground mt-1">Power-Back AI Assistant</p>
          </div>
          <form onSubmit={submitToken} className="space-y-4">
            <div>
              <Label htmlFor="admin-token" className="text-sm font-semibold">Admin token</Label>
              <Input
                id="admin-token"
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="tybk-admin-…"
                autoFocus
                data-testid="admin-token-input"
                className="mt-2"
              />
            </div>
            <Button
              type="submit"
              disabled={authChecking}
              data-testid="admin-token-submit"
              className="w-full bg-gradient-to-r from-maroon to-primary text-primary-foreground font-semibold h-11"
            >
              {authChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unlock PB'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // ---------- Main app ----------
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-primary/5" data-testid="pb-admin-page">
      <header className="border-b border-silver/20 bg-card/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-maroon to-primary flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-foreground leading-tight">PB</h1>
              <p className="text-xs text-muted-foreground -mt-0.5">Your Power-Back Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={newSession} data-testid="pb-new-session-button">
              <Plus className="w-4 h-4 mr-1.5" /> New chat
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="pb-logout-button">
              <Lock className="w-4 h-4 mr-1.5" /> Lock
            </Button>
          </div>
        </div>
      </header>

      <Tabs defaultValue="chat" className="container mx-auto px-4 py-6 max-w-5xl">
        <TabsList className="mb-6 bg-card border border-silver/20">
          <TabsTrigger value="chat" data-testid="pb-tab-chat" className="data-[state=active]:bg-maroon data-[state=active]:text-primary-foreground">
            <MessageSquare className="w-4 h-4 mr-1.5" /> Chat
          </TabsTrigger>
          <TabsTrigger value="queue" data-testid="pb-tab-queue" className="data-[state=active]:bg-maroon data-[state=active]:text-primary-foreground">
            <ListChecks className="w-4 h-4 mr-1.5" /> Post Queue
            {queue.filter((p) => p.status !== 'posted').length > 0 && (
              <Badge className="ml-2 bg-secondary text-secondary-foreground" data-testid="pb-queue-badge">
                {queue.filter((p) => p.status !== 'posted').length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="pb-tab-knowledge" className="data-[state=active]:bg-maroon data-[state=active]:text-primary-foreground">
            <Library className="w-4 h-4 mr-1.5" /> Library
          </TabsTrigger>
          <TabsTrigger value="briefing" data-testid="pb-tab-briefing" className="data-[state=active]:bg-maroon data-[state=active]:text-primary-foreground">
            <Sunrise className="w-4 h-4 mr-1.5" /> Daily Briefing
          </TabsTrigger>
          <TabsTrigger value="content360" data-testid="pb-tab-content360" className="data-[state=active]:bg-maroon data-[state=active]:text-primary-foreground">
            <Globe className="w-4 h-4 mr-1.5" /> Content360
            {content360Events.filter((e) => e.event_type?.includes('publish')).length > 0 && (
              <Badge className="ml-2 bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-testid="pb-content360-badge">
                {content360Events.filter((e) => e.event_type?.includes('publish')).length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* CHAT TAB */}
        <TabsContent value="chat" className="m-0">
          <Card className="border-silver/20 overflow-hidden" data-testid="pb-chat-card">
            <div
              ref={scrollRef}
              className="h-[calc(100vh-320px)] min-h-[400px] overflow-y-auto p-5 space-y-4 bg-gradient-to-br from-background to-primary/[0.03]"
              data-testid="pb-chat-scroll"
            >
              {messages.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-maroon via-primary to-secondary mx-auto mb-4 flex items-center justify-center shadow-lg">
                    <Sparkles className="w-7 h-7 text-primary-foreground" />
                  </div>
                  <h2 className="font-display text-2xl font-bold text-foreground mb-2">Hi Breah, I'm PB.</h2>
                  <p className="text-muted-foreground text-sm mb-8 max-w-md mx-auto">
                    Your Power-Back Assistant. Drafts, calendars, copy, strategy — ask me anything or tap a prompt below.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                    {QUICK_PROMPTS.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => send(p)}
                        data-testid={`pb-quick-prompt-${i}`}
                        className="px-4 py-2 text-xs rounded-full bg-card border border-silver/30 hover:border-maroon/50 hover:bg-maroon/5 transition-all text-foreground font-medium"
                      >
                        {p.length > 60 ? p.slice(0, 58) + '…' : p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <AnimatePresence initial={false}>
                {messages.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                        m.role === 'user'
                          ? 'bg-gradient-to-br from-maroon to-primary text-primary-foreground rounded-br-sm'
                          : 'bg-card border border-silver/25 text-foreground rounded-bl-sm shadow-sm'
                      }`}
                      data-testid={`pb-msg-${m.role}-${i}`}
                    >
                      <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed m-0">{m.content}</pre>
                      {m.queued?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-silver/30 text-xs text-secondary font-semibold flex items-center gap-1.5">
                          <ListChecks className="w-3.5 h-3.5" /> {m.queued.length} added to your queue
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              {sending && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl bg-card border border-silver/25 rounded-bl-sm">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader2 className="w-4 h-4 animate-spin text-maroon" /> PB is thinking…
                    </div>
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-silver/20 p-4 bg-card flex gap-3"
            >
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask PB anything… (Shift+Enter for new line)"
                rows={2}
                disabled={sending}
                data-testid="pb-chat-input"
                className="flex-1 resize-none border-silver/30 focus-visible:ring-maroon/40"
              />
              <Button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="pb-chat-send-button"
                className="bg-gradient-to-r from-maroon to-primary text-primary-foreground font-semibold h-auto px-5"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </Card>
        </TabsContent>

        {/* QUEUE TAB */}
        <TabsContent value="queue" className="m-0 space-y-4">
          <Card className="p-5 border-silver/20" data-testid="pb-make-webhook-card">
            <div className="flex items-start gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-maroon to-primary flex items-center justify-center text-primary-foreground flex-shrink-0">
                <Send className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-0.5">Auto-posting via Zapier or Make.com → Buffer</h3>
                {makeWebhook ? (
                  <p className="text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1 text-primary"><Check className="w-3 h-3" /> Connected.</span>
                    {' '}Click "Auto-post via webhook" on any post to send it to your socials.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">Not configured yet. Paste your Zapier (or Make.com) webhook URL below to enable one-click auto-posting.</p>
                )}
              </div>
            </div>
            {!makeWebhook && (
              <form onSubmit={saveMakeWebhook} className="flex gap-2 mt-3">
                <Input
                  value={makeWebhookInput}
                  onChange={(e) => setMakeWebhookInput(e.target.value)}
                  placeholder="https://hooks.zapier.com/hooks/catch/… or https://hook.us2.make.com/…"
                  data-testid="pb-make-webhook-input"
                  className="text-sm font-mono"
                />
                <Button
                  type="submit"
                  data-testid="pb-make-webhook-save"
                  className="bg-gradient-to-r from-maroon to-primary text-primary-foreground font-semibold"
                >
                  Save
                </Button>
              </form>
            )}
          </Card>

          {queue.length === 0 ? (
            <Card className="p-12 text-center" data-testid="pb-queue-empty">
              <ListChecks className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
              <h2 className="font-display text-xl font-bold text-foreground mb-1">Your queue is empty</h2>
              <p className="text-sm text-muted-foreground">Ask PB to draft a post and add it here.</p>
            </Card>
          ) : (
            queue.map((p) => {
              const Icon = PLATFORM_ICON[p.platform] || Sparkles;
              return (
                <Card key={p.id} className="p-5 border-silver/20" data-testid={`pb-queue-item-${p.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-maroon flex items-center justify-center text-primary-foreground">
                        <Icon className="w-4.5 h-4.5" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground text-sm">{p.platform}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {p.scheduled_for === 'draft' ? 'Draft (no schedule)' : p.scheduled_for}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={p.status === 'posted' ? 'default' : 'outline'}
                      className={p.status === 'posted' ? 'bg-primary/20 text-primary border-primary/30' : 'border-secondary/50 text-secondary'}
                    >
                      {p.status}
                    </Badge>
                  </div>
                  <pre className="whitespace-pre-wrap font-body text-sm text-foreground leading-relaxed bg-background/50 rounded-lg p-3 border border-silver/20 mb-3" data-testid={`pb-queue-caption-${p.id}`}>
                    {p.caption}
                  </pre>
                  {p.image_note && p.image_note !== 'none' && (
                    <div className="text-xs text-muted-foreground bg-secondary/10 border border-secondary/30 rounded-md px-3 py-2 mb-3 flex items-start gap-2">
                      <ImagePlus className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-secondary" />
                      <span><strong className="text-foreground">Image:</strong> {p.image_note}</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => copyText(p.caption, p.id)}
                      data-testid={`pb-queue-copy-${p.id}`}
                      className="bg-card border border-silver/30 text-foreground hover:bg-maroon/5"
                      variant="outline"
                    >
                      {copiedId === p.id ? <Check className="w-3.5 h-3.5 mr-1.5 text-primary" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                      {copiedId === p.id ? 'Copied!' : 'Copy caption'}
                    </Button>
                    {p.status !== 'posted' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => markPosted(p.id)}
                          data-testid={`pb-queue-posted-${p.id}`}
                          className="bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20"
                          variant="outline"
                        >
                          <Check className="w-3.5 h-3.5 mr-1.5" /> Mark posted
                        </Button>
                        {makeWebhook && (
                          <Button
                            size="sm"
                            onClick={() => dispatchToMake(p.id)}
                            disabled={dispatchingId === p.id}
                            data-testid={`pb-queue-dispatch-${p.id}`}
                            className="bg-gradient-to-r from-maroon to-primary text-primary-foreground font-semibold"
                          >
                            {dispatchingId === p.id ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            ) : (
                              <Send className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            Auto-post
                          </Button>
                        )}
                      </>
                    )}
                    <Button
                      size="sm"
                      onClick={() => deletePost(p.id)}
                      data-testid={`pb-queue-delete-${p.id}`}
                      variant="ghost"
                      className="text-muted-foreground hover:text-maroon hover:bg-maroon/5 ml-auto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* LIBRARY TAB */}
        <TabsContent value="knowledge" className="m-0 space-y-5">
          <Card className="p-6 border-silver/20" data-testid="pb-knowledge-upload-card">
            <h2 className="font-display text-lg font-bold text-foreground mb-1">Feed PB a new source</h2>
            <p className="text-sm text-muted-foreground mb-4">
              <strong>PDFs:</strong> drop any PDF below — PB extracts and indexes the text automatically.<br />
              <strong>Text/Markdown:</strong> paste raw text into the form below (for transcripts, your own notes, web articles).
            </p>

            <div className="mb-5 p-4 rounded-xl border-2 border-dashed border-maroon/30 bg-maroon/5">
              <label className="flex flex-col items-center justify-center cursor-pointer text-center" data-testid="pb-pdf-upload-label">
                <FileUp className="w-8 h-8 text-maroon mb-2" />
                <span className="font-semibold text-foreground text-sm">
                  {uploadingPdf ? 'Extracting text…' : 'Upload a PDF'}
                </span>
                <span className="text-xs text-muted-foreground mt-1">
                  Click or drag a .pdf file — max 30 MB
                </span>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={uploadPdf}
                  disabled={uploadingPdf}
                  data-testid="pb-pdf-upload-input"
                  className="hidden"
                />
              </label>
            </div>

            <form onSubmit={uploadKnowledge} className="space-y-3">
              <div>
                <Label htmlFor="kn-name" className="text-sm font-semibold">Source name</Label>
                <Input
                  id="kn-name"
                  value={knowledgeName}
                  onChange={(e) => setKnowledgeName(e.target.value)}
                  placeholder="e.g. duncan_youre_not_broken_chapter_1"
                  data-testid="pb-knowledge-name-input"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="kn-content" className="text-sm font-semibold">Content (paste text)</Label>
                <Textarea
                  id="kn-content"
                  value={knowledgeContent}
                  onChange={(e) => setKnowledgeContent(e.target.value)}
                  placeholder="Paste book chapters, transcripts, frameworks, your own notes…"
                  rows={8}
                  data-testid="pb-knowledge-content-input"
                  className="mt-2 font-body text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">{knowledgeContent.length.toLocaleString()} characters</p>
              </div>
              <Button
                type="submit"
                data-testid="pb-knowledge-submit"
                className="bg-gradient-to-r from-maroon to-primary text-primary-foreground font-semibold"
              >
                <BookOpen className="w-4 h-4 mr-2" /> Add to PB's library
              </Button>
            </form>
          </Card>

          <Card className="p-6 border-silver/20">
            <h2 className="font-display text-lg font-bold text-foreground mb-3">Loaded sources</h2>
            {knowledge.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No sources yet — PB will work from the built-in Magnetic Mind Method foundation.</p>
            ) : (
              <ul className="space-y-2" data-testid="pb-knowledge-list">
                {knowledge.map((k) => (
                  <li key={k.filename} className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-silver/20">
                    <div className="flex items-center gap-2.5">
                      <BookOpen className="w-4 h-4 text-maroon" />
                      <span className="text-sm font-medium text-foreground">{k.filename}</span>
                      <span className="text-xs text-muted-foreground">{(k.size_bytes / 1024).toFixed(1)} KB</span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteKnowledge(k.filename)}
                      data-testid={`pb-knowledge-delete-${k.filename}`}
                      className="text-muted-foreground hover:text-maroon"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        {/* DAILY BRIEFING TAB */}
        <TabsContent value="briefing" className="m-0 space-y-5">
          <Card className="p-6 border-silver/20 overflow-hidden relative" data-testid="pb-briefing-card">
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
              backgroundImage: 'radial-gradient(circle at 20% 30%, #c89436, transparent 50%), radial-gradient(circle at 80% 70%, #7a1a36, transparent 50%)',
            }} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-secondary via-maroon to-primary flex items-center justify-center shadow-md">
                  <Sunrise className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-bold text-foreground">Daily Briefing</h2>
                  <p className="text-sm text-muted-foreground">One post · one strategic move · one anchor — generated fresh from your library</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-5 mb-4">
                <div>
                  <Label htmlFor="briefing-focus" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What's the focus this week? (optional)</Label>
                  <Textarea
                    id="briefing-focus"
                    value={briefingFocus}
                    onChange={(e) => setBriefingFocus(e.target.value)}
                    placeholder="e.g. growing the free bundle list · filling Collective founding spots · launching the affiliate program"
                    rows={2}
                    data-testid="pb-briefing-focus-input"
                    className="mt-1.5 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="briefing-email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email it to</Label>
                  <Input
                    id="briefing-email"
                    type="email"
                    value={briefingEmail}
                    onChange={(e) => setBriefingEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="pb-briefing-email-input"
                    className="mt-1.5 text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leave empty to just view it here</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => generateBriefing(false)}
                  disabled={briefingLoading}
                  data-testid="pb-briefing-generate-button"
                  className="bg-gradient-to-r from-maroon to-primary text-primary-foreground font-semibold"
                >
                  {briefingLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sunrise className="w-4 h-4 mr-2" />}
                  Generate today's briefing
                </Button>
                <Button
                  onClick={() => generateBriefing(true)}
                  disabled={briefingLoading || !briefingEmail}
                  data-testid="pb-briefing-email-button"
                  variant="outline"
                  className="border-secondary/50 text-foreground"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Generate &amp; email me
                </Button>
              </div>
            </div>
          </Card>

          {briefing && (
            <Card className="p-6 border-silver/20" data-testid="pb-briefing-result">
              <div className="flex items-start justify-between gap-3 mb-3">
                <p className="text-xs uppercase tracking-widest text-secondary font-semibold">
                  {briefing.date}
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyText(briefing.briefing, 'briefing')}
                  data-testid="pb-briefing-copy-button"
                >
                  {copiedId === 'briefing' ? <Check className="w-3.5 h-3.5 mr-1.5 text-primary" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                  {copiedId === 'briefing' ? 'Copied' : 'Copy all'}
                </Button>
              </div>
              <pre className="whitespace-pre-wrap font-body text-sm text-foreground leading-relaxed">{briefing.briefing}</pre>
              {briefing.queued_posts?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-silver/20 text-xs text-secondary font-semibold flex items-center gap-1.5">
                  <ListChecks className="w-3.5 h-3.5" /> Post automatically added to your Ready-to-Post Queue
                </div>
              )}
            </Card>
          )}
        </TabsContent>

        {/* CONTENT360 TAB */}
        <TabsContent value="content360" className="m-0 space-y-5">
          <Card className="p-6 bg-card border-silver/20">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="w-5 h-5 text-secondary mt-0.5" />
              <div className="flex-1">
                <h2 className="font-heading text-xl text-foreground mb-1">Content360 Activity Feed</h2>
                <p className="text-sm text-muted-foreground">
                  Real-time events from your Content360 dashboard — every post scheduled, published, or failed will appear here automatically.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => loadContent360Events()}
                disabled={content360Loading}
                data-testid="pb-content360-refresh-button"
              >
                {content360Loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Refresh
              </Button>
            </div>

            {/* Setup card */}
            <div className="rounded-lg bg-maroon/10 border border-maroon/30 p-4 mb-5">
              <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> One-time setup
              </p>
              <p className="text-sm text-foreground/90 mb-3">
                In Content360 → <strong>Webhook</strong> tab, click <strong>Create webhook</strong> and use this Callback URL:
              </p>
              <div className="flex items-center gap-2 bg-background border border-silver/30 rounded px-3 py-2 font-mono text-xs">
                <code className="flex-1 truncate text-foreground" data-testid="pb-content360-webhook-url">
                  {`${process.env.REACT_APP_BACKEND_URL}/api/admin/assistant/content360-webhook`}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/assistant/content360-webhook`;
                    navigator.clipboard.writeText(url);
                    toast.success('Callback URL copied');
                  }}
                  data-testid="pb-content360-copy-url-button"
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                Method: <code className="text-secondary">POST</code> · Content-Type: <code className="text-secondary">application/json</code> · Check ALL event boxes (Account Added/Updated/Deleted, Post Scheduled/Published/Deleted, Publishing Post Failed) → Create.
              </p>
            </div>

            {/* Events list */}
            {content360Events.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="pb-content360-empty">
                <Globe className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No events yet. Set up the webhook above, then post something in Content360.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="pb-content360-events-list">
                {content360Events.map((ev) => {
                  const isPublished = ev.event_type?.includes('publish') && !ev.event_type?.includes('fail');
                  const isFailed = ev.event_type?.includes('fail');
                  const isScheduled = ev.event_type?.includes('schedule');
                  const Icon = isPublished ? CheckCircle2 : isFailed ? XCircle : isScheduled ? Clock : Globe;
                  const iconColor = isPublished ? 'text-emerald-400' : isFailed ? 'text-red-400' : isScheduled ? 'text-amber-400' : 'text-secondary';
                  return (
                    <div
                      key={ev.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-silver/15 bg-background/40 hover:border-secondary/40 transition-colors"
                      data-testid={`pb-content360-event-${ev.id}`}
                    >
                      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconColor}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-sm font-semibold text-foreground capitalize">
                            {ev.event_type?.replace(/_/g, ' ') || 'event'}
                          </span>
                          {ev.platform && (
                            <Badge variant="outline" className="text-[10px] capitalize border-secondary/30 text-secondary">
                              {ev.platform}
                            </Badge>
                          )}
                        </div>
                        {ev.caption_preview && (
                          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                            {ev.caption_preview}
                          </p>
                        )}
                        <p className="text-[10px] text-muted-foreground/70 mt-1">
                          {new Date(ev.received_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
